import React, { useEffect, useState } from 'react';
import { MapContainer, WMSTileLayer, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-side-by-side';
import 'leaflet-draw/dist/leaflet.draw.css';
import { getCenterAndZoom } from './custom/MapFunctions';
import ExportControl from './custom/MapExportLayers';
import GeoJsonControl from './custom/MapFranceCaseStudyLayers';
import SpainControl from './custom/MapSpainLayers';
import LeafletControlGeocoder from './custom/MapSearch';
import { HistoricalMapControl } from './custom/MapFranceHisotricalLayers';
import { Radio, Typography } from 'antd';
import BaseMapLayerControl from './custom/MapChangeMapLayer';
import Cookies from 'js-cookie';
import { RailwayMapControl } from './custom/MapFranceRailwayLayers';

const { Text } = Typography;

interface IndicatorsMapProps {
  indicatorAlias?: string;
}

const IndicatorsMap: React.FC<IndicatorsMapProps> = ({ indicatorAlias }) => {
  const caseStudy = Cookies.get('caseStudy');
  const [exportControlRef, setExportControlRef] = useState<any>(null);
  const [geoJsonControlRef, setGeoJsonControlRef] = useState<any>(null);
  const [spainControlRef, setSpainControlRef] = useState<any>(null);
  const [franceHistoricalControlRef, setFranceHistoricalControlRef] = useState<any>(null);
  const [exportRailwayLayerRef, setExportRailwayLayerRef] = useState<any>(null);

  // We'll store opacity as a number from 0 to 10; 0 => 0%, 10 => 100%.
  const [opacity, setOpacity] = useState(5);

  const [indicatorLayerConfig, setIndicatorLayerConfig] = useState<{
    key: string;
    url: string;
    layers: string;
    zIndex: number;
  } | null>(null);

  // Update map configuration when a new indicator is selected
  useEffect(() => {
    if (caseStudy && indicatorAlias) {
      setIndicatorLayerConfig({
        key: `${caseStudy}_indicators_${indicatorAlias}`,
        url: `https://maps.plantnet.org/guarden/${caseStudy}/indicators?`,
        layers: `${indicatorAlias}`,
        zIndex: 1000,
      });
    }
  }, [caseStudy, indicatorAlias]);

  const handleOpacityChange = (newOpacity: number) => {
    setOpacity(newOpacity); // Update opacity state
  };

  // Map Initialization Component
  const MapInitializer: React.FC = () => {
    const map = useMap();

    useEffect(() => {
      if (caseStudy) {
        const { center, zoom } = getCenterAndZoom(caseStudy);
        map.setView(center, zoom);
      }

      // Add custom controls if they haven't been added yet
      if (!exportControlRef) {
        const exportControl = new ExportControl({ position: 'topright' });
        exportControl.addTo(map);
        setExportControlRef(exportControl);
      }

      if (!geoJsonControlRef) {
        const geoJsonControl = new GeoJsonControl({ position: 'bottomleft' });
        geoJsonControl.addTo(map);
        setGeoJsonControlRef(geoJsonControl);
      }

      if (!spainControlRef && caseStudy === 'barcelona') {
        const spainControl = new SpainControl({ position: 'bottomleft' });
        spainControl.addTo(map);
        setSpainControlRef(spainControl);
      }

      if (!franceHistoricalControlRef && caseStudy === 'pnr_narbonnaise') {
        const franceHistoricalControl = new HistoricalMapControl({ position: 'bottomleft' });
        franceHistoricalControl.addTo(map);
        setFranceHistoricalControlRef(franceHistoricalControl);
      }
      if (!exportRailwayLayerRef && caseStudy === 'pnr_narbonnaise') {
        const exportRailwayLayer = new RailwayMapControl({ position: 'bottomleft' });
        exportRailwayLayer.addTo(map);
        setExportRailwayLayerRef(exportRailwayLayer);
      }
    }, [map]);

    return null;
  };

  return (
    <div id='indicatorMap'>
      <MapContainer center={[0, 0]} zoom={0} style={{ height: '800px', width: '100%', borderRadius: '10px' }}>
        <LeafletControlGeocoder />
        <MapInitializer />
        <BaseMapLayerControl />
        {/* Case Study Layer */}
        {caseStudy && (
          <WMSTileLayer
            key={`${caseStudy}`}
            url={`https://maps.plantnet.org/guarden/${caseStudy}/indicators`}
            layers={'extent'}
            format='image/png'
            transparent={true}
            version='1.3.0'
            opacity={0.5}
            attribution='© Guarden'
            zIndex={999}
          />
        )}
        {/* Indicator Layer (Dynamically Updates) */}
        {indicatorLayerConfig && (
          <WMSTileLayer
            key={indicatorLayerConfig.key}
            url={indicatorLayerConfig.url}
            layers={indicatorLayerConfig.layers}
            format='image/png'
            transparent={true}
            version='1.3.0'
            attribution='© Guarden'
            zIndex={indicatorLayerConfig.zIndex}
            opacity={opacity / 10}
          />
        )}
        {/* Legend + Opacity Controls */}
        <IndicatorLegend
          opacity={opacity}
          onOpacityChange={handleOpacityChange}
          caseStudy={caseStudy}
          indicatorAlias={indicatorAlias}
        />
      </MapContainer>
    </div>
  );
};

interface IndicatorLegendProps {
  opacity: number;
  onOpacityChange: (opacity: number) => void;
  caseStudy?: string;
  indicatorAlias?: string;
}

const IndicatorLegend: React.FC<IndicatorLegendProps> = ({ opacity, onOpacityChange, caseStudy, indicatorAlias }) => {
  // This flag determines whether to show the fetched legend graphic or fall back to the static legend.
  const [legendGraphicAvailable, setLegendGraphicAvailable] = useState(true);

  // Build the legend graphic URL for the currently selected indicator.
  const legendUrl = `https://maps.plantnet.org/guarden/${caseStudy}/indicators?SERVICE=WMS&REQUEST=GetLegendGraphic&VERSION=1.3.0&FORMAT=image/png&LAYER=${indicatorAlias}`;

  const handleRadioChange = (e: any) => {
    const newOpacity = Number(e.target.value);
    onOpacityChange(newOpacity);
  };

  return (
    <div
      id='indicatorLegend'
      style={{
        padding: '12px',
        background: '#ffffff',
        borderRadius: '8px',
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.15)',
        position: 'absolute',
        top: '100px',
        right: '10px',
        zIndex: 401,
        width: '250px',
        fontFamily: 'Arial, sans-serif',
      }}
    >
      <strong style={{ fontSize: '16px', display: 'block', marginBottom: '10px' }}>Layer Legend</strong>

      {/* Legend Graphic */}
      <div style={{ marginBottom: '15px', textAlign: 'center' }}>
        {legendGraphicAvailable ? (
          <img
            src={legendUrl}
            alt='Legend Graphic'
            onError={() => setLegendGraphicAvailable(false)}
            style={{
              width: '100%',
              borderRadius: '5px',
              border: '1px solid #ddd',
              padding: '4px',
              backgroundColor: '#f9f9f9',
            }}
          />
        ) : (
          <div style={{ fontStyle: 'italic', color: '#777' }}>The legend graphic isn't available</div>
        )}
      </div>

      {/* Opacity Controls */}
      <div style={{ marginBottom: '15px' }}>
        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Opacity</label>
        <Radio.Group
          options={[
            { label: '0%', value: 0 },
            { label: '25%', value: 2.5 },
            { label: '50%', value: 5 },
            { label: '75%', value: 7.5 },
            { label: '100%', value: 10 },
          ]}
          onChange={handleRadioChange}
          value={opacity}
          optionType='button'
          buttonStyle='solid'
        />
      </div>

      {/* Layers Info */}
      <div
        style={{
          padding: '8px',
          background: '#f1f1f1',
          borderRadius: '5px',
          fontSize: '14px',
          color: '#333',
        }}
      >
        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Info</label>
        <Text>The indicators concern flora biodiversity</Text>
      </div>
    </div>
  );
};

export { IndicatorsMap };
